import { h, Component } from 'preact';
import $ from '../utils/net';
import OriginalConfig from './config';
import LFeed from './live-feed';
import { makeReactive } from '../utils/reactive-store';

const Config = OriginalConfig as any as {
    softionAPI: string;
    lang: string;
    betting: Record<string, any>;
};

const toDate = (d: Date): string => d.toISOString().slice(0, 10);
const todayDate = (): string => toDate(new Date());

export type TicketStatus = "win" | "lose" | "void" | "half-win" | "half-lose" | null;

const LIVE_STATUSES: Record<string, TicketStatus> = { "W": "win", "L": "lose", "X": "void", "H": "half-win", "Z": "half-lose", "false": null };
const PRE_STATUSES: Record<string, TicketStatus> = { "V": "win", "P": "lose", "X": "void", "H": "half-win", "Z": "half-lose", "false": null };

interface OddOutcome {
    id: string;
    eid: string;
    name: string;
    market: string;
    outcome: string;
    live: boolean;
    value: number;
    status: TicketStatus;
    date: number;
    legatura: number[];
    sport: { name: string };
    category: { name: string };
    tournament: { name: string };
}

interface NormalizedTicket {
    ticketId: string;
    id: string | number;
    player: string | number;
    betAmount: number;
    maxWinAmount: number;
    winAmount: number;
    currency: string;
    odds: OddOutcome[];
    live: boolean;
    mixed: boolean;
    date: number;
    cashout: boolean;
    autocashout: boolean;
    status: string;
    totOdd: number;
}

interface CashoutConfig {
    basePenalty: number;
    penaltyPerEvent: number;
    baseLivePenalty: number;
    disablePreWithLiveOdds: boolean;
}

/**
 * Ti da la data "date", letta come se fosse nel fuso orario locale e la porta al fuso orario tzString
*/
function myDateInThisTimezone(date: string | Date, tzString: string): Date {
	return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", { timeZone: tzString, hour12: false }));
}

class TzAwareDateParser {
	delta: number = 0; // orologio in tz locale - orologio in tz scelta (Roma - Bogota)
	constructor(tzName: string) {
		const now = new Date();
		this.delta = now.getTime() - myDateInThisTimezone(now, tzName).getTime();
	}
	parseWithImplicitTZ(dateStr: string): Date {
		const onMyWall = new Date(dateStr).getTime(); // Le 22 a roma diventa 22 ora Bogota
		const offsettedTime = onMyWall + this.delta
		return new Date(offsettedTime)
	}
}

class CashoutService {
	static dateParser = new TzAwareDateParser('Europe/Rome');
	static _state = makeReactive({
		updatedOddsInLiveTickets: {} as Record<string, number>,
		updatedOddsInPrematchTickets: {} as Record<string, number>,
		betsList: [] as NormalizedTicket[],
	})
	static config: CashoutConfig = makeReactive({
		basePenalty: 0.2,
		penaltyPerEvent: 0.1,
		baseLivePenalty: 0.1,
		disablePreWithLiveOdds: false // Ancora non esiste lato API, ma lo metto qua così quando sarà il momento entra in azione
	});
	
	static get updatedOddsInLiveTickets(): Record<string, number> {return this._state.updatedOddsInLiveTickets}
	static set updatedOddsInLiveTickets(v) {this._state.updatedOddsInLiveTickets = v}
	static get updatedOddsInPrematchTickets(): Record<string, number> {return this._state.updatedOddsInPrematchTickets}
	static set updatedOddsInPrematchTickets(v) {this._state.updatedOddsInPrematchTickets = v}
	static get betsList(): NormalizedTicket[] {return this._state.betsList}
	static set betsList(v) {this._state.betsList = v}

	static fetchOpenBets(): Promise<NormalizedTicket[]> {
		return new Promise((res, rej) => {
			$.getJSON(Config.softionAPI + "/user/verificaScommesse?json&searchStatus=N&searchBetType=T&searchHelper=0&l=" + Config.lang, (err: any, data: any) => {
				if (!!err)
					return rej(err)

				const tickets = (data.bets as any[]).map(bet => CashoutService.normalizeTicket(bet));
				this.betsList = tickets;

				res(tickets);
			})
		})
	}
	static fetchClosedBets(): Promise<any[]> {
		return Promise.resolve([])
	}
	static loadConfig(): void {
		CashoutService.config.basePenalty = 1 - parseInt(Config.betting['cashout-base']) / 100;
		CashoutService.config.penaltyPerEvent = parseInt(Config.betting['cashout-penalty']) / 100;
		CashoutService.config.baseLivePenalty = parseInt(Config.betting['cashout-live-commission']) / 100;
		CashoutService.config.disablePreWithLiveOdds = !!Config.betting['disable-cashout-prematch-and-live'];
	}

	static normalizeTicket(ticket: any): NormalizedTicket {
        const rawDettaglio = typeof ticket.dettaglio === 'string' ? JSON.parse(ticket.dettaglio) : ticket.dettaglio;
		const normalizedTicket: NormalizedTicket = {
			ticketId: ticket.id,
			id: ticket.idbets,
			player: ticket.idaccounts,
			// NOTE: l'API di verificaScommesse aliasa b.display_importo_scommesso AS importo_scommesso.
			// Tutti gli importi (betAmount, winAmount, maxWinAmount) sono in valuta display (ETB), NON USD.
			betAmount: parseFloat(ticket.importo_scommesso),
			maxWinAmount: parseFloat(ticket.prob_vincita),
			winAmount: parseFloat(ticket.importo_vincita),
			currency: ticket.display_currency,
			odds: [],
			live: ticket.codtipo == 'L',
			mixed: ticket.codtipo == 'R',
			date: CashoutService.dateParser.parseWithImplicitTZ(ticket.data_scommessa).getTime(),
			cashout: !!ticket.cashout || ticket.codtipo == 'L',
			autocashout: !!ticket.autocashout,
			status: ticket.stato,
			totOdd: 1
		};
		if (Array.isArray(rawDettaglio)) { // è un ticket live con quote in forma array
			normalizedTicket.odds = rawDettaglio.map((oddRow: any) => ({
				id: oddRow.id,
				eid: oddRow.id.split('-')[0],
				name: oddRow.event.name,
				market: oddRow.outcome.market,
				outcome: oddRow.outcome.name,
				live: true,
				value: oddRow.outcome.odd,
				status: LIVE_STATUSES[String(oddRow.outcome.status)],
				date: CashoutService.dateParser.parseWithImplicitTZ(oddRow.event.date).getTime(),
				legatura: (oddRow.outcome.rule as string).split('-').map(l => parseInt(l)),
				sport: { name: oddRow.event.sport },
				category: { name: oddRow.event.category },
				tournament: { name: oddRow.event.tournament },
			}));
		} else { // è un ticket qualsiasi con le quote in un oggetto
			const prematchParents = (oddRow: any) => {
				const [sportName, categoryName, tournamentName] = (oddRow.campionato as string).split(' - ', 3);
				return {
					sport: { name: sportName },
					category: { name: categoryName },
					tournament: { name: tournamentName },
				};
			}
			normalizedTicket.odds = Object.entries(rawDettaglio as Record<string, any>).map(([id, oddRow]) => ({
				id: !!oddRow['live-id'] ? oddRow['categoryid'] + '-' + oddRow['live-id'] : id,
				eid: id.split('-')[1],
				name: oddRow.evento,
				market: oddRow.scommessa,
				outcome: oddRow.segno,
				live: false,
				value: parseFloat(oddRow.quota),
				status: !!oddRow.status ? PRE_STATUSES[String(oddRow.status)] : null,
				date: CashoutService.dateParser.parseWithImplicitTZ(oddRow.data_evento).getTime(), // Le live non hanno data di inizio
				legatura: (oddRow.legatura as string).split('-').map(l => parseInt(l)),
				...prematchParents(oddRow)
			}));
		}
		normalizedTicket.totOdd = normalizedTicket.odds.reduce((c, outcome) => c * outcome.value, 1);
		return normalizedTicket;
	}

	/**
	 * Non usare: Usa LFeed.getEvent("/event/000000"). Questo serviva al template per recuperare solo gli eventi dei ticket e mostrare il campetto.
	 * Non ha senso usarlo perché puoi semplicemente usare LFeed#getEvent() per avere l'evento dove lo necessiti.
	 * Lo lascio solo perché è un'indirezione che magari aiuta in caso smettiamo di inviare tutti gli eventi sul websocket
	 */
	static fetchLiveEventDataFromTickets(): Record<string, any> {
		const uniqueEventIds = new Set(this.betsList.flatMap(ticket => ticket.odds).map(odd => '/event/' + odd.eid));
		const filteredLiveEvents = Object.entries((LFeed as any).events).filter(([key]) => uniqueEventIds.has(key));
		return Object.fromEntries(filteredLiveEvents);
	}

	static fetchCurrentOddsForLiveTickets(tickets: NormalizedTicket[]): Promise<void> {
		// Fai la chiamata API
		// POST /newlive/cashout?json
		// action=refresh&code[0]=609BD50B72E4BB6EA730
		return new Promise<any>((res, rej) => $.post(Config.softionAPI + "/newlive/cashout?json", {
			body: { action: 'refresh', code: tickets.map(ticket => ticket.ticketId) },
			json: true
		}, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then(respData => {
			const odds = respData.data as Record<string, any>;
			CashoutService.updatedOddsInLiveTickets = Object.fromEntries(Object.values(odds).map(odd => [odd.event + '-' + odd.market + '-' + odd.outcome, odd.value]))
		}).catch(() => { /* oops */ });
	}

	static fetchCurrentOddsForPrematchTickets(tickets: NormalizedTicket[]): Promise<void | null> {
		if (CashoutService.config.disablePreWithLiveOdds) {
			return Promise.resolve(null);
		}
		// Fai la chiamata API
		// POST /schedina/prematchMergeCashout/?json
		// code[0]=609BD50B72E4BB6EA730
		return new Promise<any>((res, rej) => $.post(Config.softionAPI + "/schedina/prematchMergeCashout?json", {
			body: { code: tickets.map(ticket => ticket.ticketId) },
			json: true
		}, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then(respData => {
			const odds = respData.data as Record<string, any>;
			CashoutService.updatedOddsInPrematchTickets = Object.fromEntries(Object.values(odds).map(odd => [odd['betting-code'], odd.value]))
		}).catch(() => { /* oops */ });
	}

	static doPartialCashout(ticket: NormalizedTicket, partialBetAmount: number): Promise<any> {
		return new Promise((res, rej) => $.post(
			Config.softionAPI + "/ticketcashout/makePartialCashout/?json", {
			body: { idbets: ticket.ticketId, amount: partialBetAmount },
			json: true
		}, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then((respData: any) => {
			if (!!respData.error) {
				throw respData.error;
			}

			return {
				ticketId: ticket.ticketId,
				winAmount: respData.cashout.win,
				currency: ticket.currency
			}
		});
	}

	static doAutoCashout(ticket: NormalizedTicket, min: number | null, max: number | null, cancel: boolean = false): Promise<any> {
		const body: Record<string, any> = { idbets: ticket.ticketId };

		if (!!min) {
			body['min_win'] = min;
		}
		if (!!max) {
			body['max_win'] = max;
		}
		if (!!cancel) {
			body['delete'] = true;
		}

		return new Promise((res, rej) => $.post(
			Config.softionAPI + "/ticketcashout/saveAutoCashout/?json", {
			body,
			json: true
		}, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then((respData: any) => {
			if (!!respData.error) {
				throw respData.error;
			}

			return {
				ticketId: ticket.ticketId
			}
		});
	}

	static getAutoCashoutInfo(ticket: NormalizedTicket): Promise<any> {
		return new Promise((res, rej) => $.getJSON(
			Config.softionAPI + "/ticketcashout/getAutoCashout/" + ticket.ticketId + "?json", (err: any, data: any) => {
				return err ? rej(err) : res(data);
			})).then((respData: any) => {
				if (!!respData.error) {
					throw respData.error;
				}

				return {
					ticketId: ticket.ticketId,
					min: respData.autocashout.min,
					max: respData.autocashout.max,
					created: respData.autocashout.created
				}
			});
	}

	static doCashout(ticket: NormalizedTicket, expectedWin: number): Promise<any> {
		return ticket.live ? CashoutService.doLiveCashout(ticket)
			: CashoutService.doPrematchCashout(ticket, expectedWin);
	}
	static doPrematchCashout(ticket: NormalizedTicket, expectedWin: number): Promise<any> {
		// Fai la chiamata API
		// /schedina/requestCashout/?json&idbets=609BD50B72E4BB6EA730
		return new Promise((res, rej) => $.getJSON(Config.softionAPI + "/schedina/requestCashout/?json&idbets=" + ticket.ticketId, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then((respData: any) => {
			if (!!respData.error) {
				throw respData.error;
			}
			// const ticketBody = JSON.parse(respData.body);
			return {
				ticketId: ticket.ticketId,
				winAmount: expectedWin, // Non arriva dalle API un dato sulla vincita...
				currency: ticket.currency
			}
		});
	}
	static doLiveCashout(ticket: NormalizedTicket): Promise<any> {
		// Fai la chiamata API
		// POST /newlive/cashout?json
		// action=perform&code=609BD50B72E4BB6EA730
		return new Promise((res, rej) => $.post(Config.softionAPI + "/newlive/cashout?json", {
			body: { action: 'perform', code: ticket.ticketId },
			json: true
		}, (err: any, data: any) => {
			return err ? rej(err) : res(data);
		})).then((respData: any) => {
			return {
				ticketId: respData.data.code,
				winAmount: respData.data.importo_vincita,
				currency: ticket.currency
			};
		});
	}

	static calculateCashoutWin(ticketObject: NormalizedTicket): number {
		if (!ticketObject.live)
			return CashoutService.calculateCashoutWinPrematch(ticketObject);
		else
			return CashoutService.calculateCashoutWinLive(ticketObject);
	}

	static calculateCashoutWinPrematch(ticketObject: NormalizedTicket): number {
		// Sul prematch è complicato perché c'è il live mescolato
		// Le quote live ancora aperte sono contate come vincenti ma a quota (oldOdd / newOdd)
		// Un finto ticket che contiene le quote prematch come fossero vincenti e live (con cashout)
		let newBaseTicket = ticketObject.odds.map(oddRow => {
			const currentOdd = CashoutService.getUpdatedOdd(oddRow.id) as number;
			const winningOdd = oddRow.date > Date.now() || oddRow.status != null ? oddRow.value // è prematch
				: currentOdd != 0 ? oddRow.value / currentOdd // è live (aperta)
					: 0; // è live (chiusa)

			const currentStatus = oddRow.status != null ? oddRow.status
				: oddRow.date > Date.now() ? null
					: winningOdd > 0 ? 'win' : 'lose';

			return { ...oddRow, status: currentStatus, value: winningOdd };
		}).filter(oddRow => oddRow.status != null);

		if (newBaseTicket.length == 0) // Sono tutte quote che devono ancora partire? Allora è un annullo con penale
			return ticketObject.betAmount * (1 - CashoutService.config.basePenalty);

		if (CashoutService.oddsListHasInvalidBindings(newBaseTicket))
			return 0;

		const potentialTotOdd = newBaseTicket.reduce((totOdd, oddRow) => {
			return totOdd * (oddRow.status == 'lose' ? 0 : oddRow.value);
		}, 1.0);
		// Ora alla quota totale applica la penale
		const remainingEvents = ticketObject.odds.length - newBaseTicket.length;
		const awardedWinPortion = (1 - CashoutService.config.basePenalty) * Math.pow(1 - CashoutService.config.penaltyPerEvent, remainingEvents);

		const winningTotOdd = potentialTotOdd <= 1
			? potentialTotOdd * awardedWinPortion // Fai un cashout ma stai per perdere i soldi. Ti devo dare una mini percentuale di questa quota
			: (1 + (potentialTotOdd - 1) * awardedWinPortion); // Fai cashout ma stai vincendo qualcosa. Ti devo restituire la giocata e dare la vincita con penale

		const baseWinAmount = ticketObject.betAmount * winningTotOdd;

		// Ai ticket prematch si dovrebbe applicare il bonus
		const bonusAmount = 0;
		return baseWinAmount + bonusAmount;
	}

	static oddsListHasInvalidBindings(odds: any[]): boolean {
		const minLegatura = Math.max(...odds.map(oddRow => oddRow.legatura[0]))
		const maxLegatura = Math.min(...odds.map(oddRow => oddRow.legatura[1]))
		return odds.length < minLegatura || odds.length > maxLegatura;
	}

	static calculateCashoutWinLive(ticketObject: NormalizedTicket): number {
		const fullWinningOdd = ticketObject.odds.reduce((totOdd, oddRow) => {
			return totOdd * (oddRow.status != 'lose' && oddRow.status != null ? oddRow.value : 1);
		}, 1.0);
		const originalMaxOdd = ticketObject.odds.reduce((totOdd, oddRow) => {
			return totOdd * (oddRow.status == null ? oddRow.value :
				oddRow.status == 'lose' ? 0 : 1);
		}, 1.0);
		const currentMaxOdd = ticketObject.odds.reduce((totOdd, oddRow) => {
			const oddValue = CashoutService.getUpdatedOdd(oddRow.id);
			return totOdd * (oddRow.status == null ? (!!oddValue ? oddValue : 0) :
				oddRow.status == 'lose' ? 0 : 1);
		}, 1.0);

		if (!originalMaxOdd || !currentMaxOdd) // Se non puoi vincere nulla sono cazzi tuoi (non comparo con zero per proteggermi dai NaN)
			return 0;

		const newTotOdd = fullWinningOdd * originalMaxOdd / currentMaxOdd * (1 - CashoutService.config.baseLivePenalty);
		const baseCashoutWin = ticketObject.betAmount * newTotOdd;
		return baseCashoutWin;
	}

	// Può ritornare undefined o 0. Dipende dal risultato delle API e da cosa usi per chiamare
	static getUpdatedOdd(oddId: string): number | undefined {
		// TODO Prima di interpellare la cache interna puoi chiedere le quote direttamente al feed live in pagina
		// (c'è una piccola porzione delle quote, ma sono le principali)
		return CashoutService.updatedOddsInLiveTickets[oddId] || CashoutService.updatedOddsInPrematchTickets[oddId];
	}

	static oddCashoutInfo(ticketOdd: OddOutcome): { status: string; name?: TicketStatus; value?: number; isLower?: boolean; isHigher?: boolean } {
		if (ticketOdd.status != null) {
			return {
				status: 'settled',
				name: ticketOdd.status
			};
		}

		if (Date.now() > ticketOdd.date) { // È live
			const currentOddValue = CashoutService.getUpdatedOdd(ticketOdd.id);
			if (!currentOddValue) {
				return {
					status: 'suspended'
				};
			}
			return {
				status: 'live',
				value: currentOddValue,
				isLower: ticketOdd.value > currentOddValue,
				isHigher: ticketOdd.value < currentOddValue
			}
		}

		return {
			status: 'default'
		};
	}

    static cashoutPrematchPrepare(code: string, cb: (data: any) => void): void {
        $.getJSON(Config.softionAPI + '/schedina/getCashoutInfo/?json&idbets=' + code, (err: any, data: any) => {
            cb(data);
        });
    }
}

export default CashoutService;